import { cn } from "@/lib/utils";
import type { QueueEntry, ReadingCode } from "@/lib/api";
import { flagFor, fmtNum, patientAgeMonths } from "@/lib/vitals";

// Most urgent first: oxygen, then blood pressure, temperature, pulse, sugar
const ORDER: ("spo2" | "bp" | "temp" | "pulse" | "rbs")[] = ["spo2", "bp", "temp", "pulse", "rbs"];
const LABEL = { spo2: "SpO2", bp: "BP", temp: "T", pulse: "Pulse", rbs: "RBS" } as const;
const UNIT = { spo2: "%", bp: "", temp: "°F", pulse: "", rbs: "" } as const;

/** Today's abnormal vitals for a queue entry, most urgent first (empty when all are in range) */
export function vitalsWarnings(entry: QueueEntry, max = 3): { key: keyof typeof LABEL; flag: "H" | "L"; text: string }[] {
    const vitals = entry.todayVitals ?? [];
    if (!vitals.length) return [];
    const ageMonths = patientAgeMonths({ date_of_birth: entry.patientDateOfBirth, age: entry.patientAge });
    const get = (code: ReadingCode) => vitals.find(v => v.code === code);
    return ORDER.map(key => {
        if (key === "bp") {
            const s = get("bp_systolic"), d = get("bp_diastolic");
            if (!s || !d) return null;
            const flag = flagFor(s, ageMonths) ?? flagFor(d, ageMonths);
            return flag ? { key, flag, text: `${fmtNum(s.value)}/${fmtNum(d.value)}` } : null;
        }
        const r = get(key);
        const flag = flagFor(r, ageMonths);
        return r && flag ? { key, flag, text: `${fmtNum(r.value)}${UNIT[key]}` } : null;
    }).filter(Boolean).slice(0, max) as { key: keyof typeof LABEL; flag: "H" | "L"; text: string }[];
}

/**
 * Red/amber chips for today's abnormal vitals on a queue row ("SpO2 88%", "BP 150/95"), so an urgent
 * patient stands out before being called in. Flags use paediatric ranges under 18. Renders nothing
 * when every reading is in range.
 */
export default function VitalsWarningChips({ entry, max = 3, className }: { entry: QueueEntry; max?: number; className?: string }) {
    const chips = vitalsWarnings(entry, max);
    if (!chips.length) return null;
    return (
        <span className={cn("inline-flex flex-wrap items-center gap-1", className)}>
            {chips.map(c => (
                <span
                    key={c.key}
                    title={`${LABEL[c.key]} ${c.text} — ${c.flag === "H" ? "high" : "low"} for age`}
                    className={cn(
                        "rounded px-1 py-px text-2xs font-semibold tabular-nums whitespace-nowrap",
                        // Low oxygen is the one reading where "low" is the emergency
                        c.flag === "H" || c.key === "spo2" ? "bg-destructive/10 text-destructive" : "bg-warning/15 text-warning",
                    )}
                >
                    {LABEL[c.key]} {c.text}
                </span>
            ))}
        </span>
    );
}
